// src/components/Chat AI/ModeSelector.js
import React, { useState } from "react";
import {
  Container,
  SpaceBetween,
  RadioGroup,
  Box,
  StatusIndicator,
  Button,
  ExpandableSection,
  Badge,
} from "@cloudscape-design/components";

const ModeSelector = ({ 
  selectedMode, 
  onModeChange, 
  agentAvailable = true,
  agentStatus = null,
  onRefreshAgent = null,
  wsConnectionStatus = null
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const modeOptions = [
    {
      value: "bedrock-agent",
      label: "Bedrock Agent (Recommended)",
      description: "Advanced AI assistant powered by AWS Bedrock Agent with intelligent knowledge base routing, contextual understanding, and optimized response generation",
      disabled: !agentAvailable
    },
    {
      value: "manual",
      label: "Manual Mode", 
      description: "Direct API access with manual data source selection and configuration control",
      disabled: false
    }
  ];

  const getAgentStatusIndicator = () => {
    if (!agentStatus) {
      return (
        <StatusIndicator type="loading">
          Checking agent status...
        </StatusIndicator>
      );
    }

    // Check both agent availability and WebSocket connection for agent mode
    if (selectedMode === "bedrock-agent") {
      if (!agentStatus.available) {
        return (
          <StatusIndicator type="error">
            Agent service unavailable
          </StatusIndicator>
        );
      }
      
      if (wsConnectionStatus) {
        if (wsConnectionStatus.isConnecting) {
          return (
            <StatusIndicator type="loading">
              Connecting to streaming service...
            </StatusIndicator>
          );
        }
        
        if (!wsConnectionStatus.isConnected) {
          return (
            <StatusIndicator type="error">
              Streaming connection failed
            </StatusIndicator>
          );
        }
      }
      
      return (
        <StatusIndicator type="success">
          Agent ready
        </StatusIndicator>
      );
    }

    if (agentStatus.available) {
      return (
        <StatusIndicator type="success">
          Agent ready
        </StatusIndicator>
      );
    }

    return (
      <StatusIndicator type="error">
        Agent unavailable
      </StatusIndicator>
    );
  };

  const getCurrentModeLabel = () => {
    const currentMode = modeOptions.find(mode => mode.value === selectedMode);
    return currentMode ? currentMode.label : "Unknown Mode";
  };

  const getCurrentModeStatus = () => {
    if (selectedMode === "bedrock-agent") {
      if (!agentStatus?.available) return "unavailable";
      if (wsConnectionStatus?.isConnecting) return "connecting";
      if (wsConnectionStatus && !wsConnectionStatus.isConnected) return "disconnected";
      return "ready";
    }
    return "active";
  };

  const getStatusColor = () => {
    if (selectedMode === "bedrock-agent") {
      if (!agentStatus?.available) return "red";
      if (wsConnectionStatus?.isConnecting) return "grey";
      if (wsConnectionStatus && !wsConnectionStatus.isConnected) return "red";
      return "green";
    }
    return "blue";
  };

  return (
    <Container>
      <ExpandableSection
        headerText={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '14px', fontWeight: '500' }}>
              AI Mode: {getCurrentModeLabel()}
            </span>
            <Badge color={getStatusColor()}>
              {getCurrentModeStatus()}
            </Badge>
          </div>
        }
        variant="footer"
        expanded={isExpanded}
        onChange={({ detail }) => setIsExpanded(detail.expanded)}
      >
        <SpaceBetween size="m">
          <Box variant="small" color="text-body-secondary">
            Choose how you want to interact with the AI system
          </Box>
          
          <RadioGroup
            value={selectedMode}
            onChange={({ detail }) => onModeChange(detail.value)}
            items={modeOptions}
          />
          
          {selectedMode === "bedrock-agent" && (
            <Box variant="awsui-key-label" padding="s">
              <SpaceBetween size="xs">
                {getAgentStatusIndicator()}
                {agentAvailable && agentStatus?.available && (
                  <Box variant="small" color="text-body-secondary">
                    <SpaceBetween size="xs">
                      <div><strong>Key Features:</strong></div>
                      <ul style={{ margin: '4px 0', paddingLeft: '16px' }}>
                        <li>Intelligent knowledge base routing and data source selection</li>
                        <li>Contextual understanding with conversation memory</li>
                        <li>Optimized response generation with real-time streaming</li>
                        <li>Automatic error handling and retry mechanisms</li>
                        <li>Enhanced security with built-in access controls</li>
                      </ul>
                      <div style={{ fontStyle: 'italic', marginTop: '8px' }}>
                        Best for: Complex queries, multi-step reasoning, and enterprise-grade reliability
                      </div>
                    </SpaceBetween>
                  </Box>
                )}
                {!agentAvailable && (
                  <Box variant="small" color="text-status-error">
                    <SpaceBetween size="xs">
                      <div><strong>Agent Unavailable</strong></div>
                      <div>
                        The Bedrock Agent service is currently unavailable. This could be due to:
                      </div>
                      <ul style={{ margin: '4px 0', paddingLeft: '16px' }}>
                        <li>Service maintenance or deployment in progress</li>
                        <li>Network connectivity issues</li>
                        <li>AWS service limitations or quotas</li>
                      </ul>
                      <div>
                        Please use Manual Mode for now or contact your administrator if the issue persists.
                      </div>
                    </SpaceBetween>
                  </Box>
                )}
                {onRefreshAgent && (
                  (agentStatus && !agentStatus.available) || 
                  (wsConnectionStatus && !wsConnectionStatus.isConnected && !wsConnectionStatus.isConnecting)
                ) && (
                  <Button
                    iconName="refresh"
                    onClick={onRefreshAgent}
                    variant="link"
                    size="small"
                  >
                    Retry Agent Connection
                  </Button>
                )}
              </SpaceBetween>
            </Box>
          )}
          
          {selectedMode === "manual" && (
            <Box variant="awsui-key-label" padding="s">
              <SpaceBetween size="xs">
                <StatusIndicator type="info">
                  Manual configuration mode active
                </StatusIndicator>
                <Box variant="small" color="text-body-secondary">
                  <SpaceBetween size="xs">
                    <div><strong>Manual Mode Features:</strong></div>
                    <ul style={{ margin: '4px 0', paddingLeft: '16px' }}>
                      <li>Direct control over data source selection</li>
                      <li>Customizable GenAI model parameters</li>
                      <li>Transparent API interactions</li>
                      <li>Fine-grained configuration options</li>
                    </ul>
                    <div>
                      Use the "Show Data Sources" and "Show GenAI Options" buttons to customize your experience.
                    </div>
                    <div style={{ fontStyle: 'italic', marginTop: '8px' }}>
                      Best for: Testing, debugging, and scenarios requiring specific configuration control
                    </div>
                  </SpaceBetween>
                </Box>
              </SpaceBetween>
            </Box>
          )}
        </SpaceBetween>
      </ExpandableSection>
    </Container>
  );
};

export default ModeSelector;